import { Assignment } from "~/classes/models/assignments/assignment.model";
import { Classroom } from "~/classes/models/classrooms/classroom.model";
import { ClassroomsService } from "~/classes/models/classrooms/classrooms.service";
import { Student } from "~/classes/models/students/student.model";
import { StudentsService } from "~/classes/models/students/students.service";

export const useDocumentClassroomData = (documentId: string) =>
  defineStore(`/document/classroom/${documentId}`, () => {
    const submittedDocumentStore = useSubmittedDocumentStore(documentId);
    const { submittedDocument } = storeToRefs(submittedDocumentStore);

    const classroom = ref<Classroom | undefined>(undefined);
    const assignment = ref<Assignment | undefined>(undefined);
    const student = ref<Student | undefined>(undefined);

    const initialize = async () => {
      if (submittedDocument.value == undefined) {
        return;
      }

      const classroomId = submittedDocument.value.classroomId;
      const assignmentId = submittedDocument.value.assignmentId;
      const studentId = submittedDocument.value.studentId;

      if (
        classroomId == undefined ||
        assignmentId == undefined ||
        studentId == undefined
      ) {
        return;
      }

      const classroomStore = useUserClassroomDataStore(classroomId);
      classroom.value =
        classroomStore.classroom ?? await ClassroomsService.get(classroomId);

      const assignmentStore = useAssignmentDataStore(classroomId, assignmentId);
      assignment.value = assignmentStore.assignment;

      student.value = await StudentsService.get(studentId);
    };

    const allowSubmissionType = computed(() => {
      return (submissionType: DocumentSubmissionType) => {
        switch (submissionType) {
          case DocumentSubmissionType.keyWordOutline:
            return assignment.value?.allowKWOSubmission != undefined
              ? assignment.value.allowKWOSubmission
              : classroom.value?.allowKWOSubmission;
          case DocumentSubmissionType.roughDraft:
            return assignment.value?.allowRoughDraftSubmission != undefined
              ? assignment.value.allowRoughDraftSubmission
              : classroom.value?.allowRoughDraftSubmission;
          case DocumentSubmissionType.finalDraft:
            return assignment.value?.allowFinalDraftSubmission;
          default:
            return true;
        }
      };
    });

    const requireSubmissionType = computed(() => {
      return (submissionType: DocumentSubmissionType) => {
        switch (submissionType) {
          case DocumentSubmissionType.keyWordOutline:
            return assignment.value?.requireKWOSubmission != undefined
              ? assignment.value.requireKWOSubmission
              : classroom.value?.requireKWOSubmission;
          case DocumentSubmissionType.roughDraft:
            return assignment.value?.requireRoughDraftSubmission != undefined
              ? assignment.value.requireRoughDraftSubmission
              : classroom.value?.requireRoughDraftSubmission;
          case DocumentSubmissionType.finalDraft:
          default:
            return true;
        }
      };
    });

    const assignmentComponents = computed(() => {
      if (assignment.value == undefined) return [];

      if (assignment.value.type == AssignmentType.general) return [];

      const components = [] as DocumentSubmissionType[];

      if (allowSubmissionType.value(DocumentSubmissionType.keyWordOutline)) {
        components.push(DocumentSubmissionType.keyWordOutline);
      }

      if (allowSubmissionType.value(DocumentSubmissionType.roughDraft)) {
        components.push(DocumentSubmissionType.roughDraft);
      }

      if (allowSubmissionType.value(DocumentSubmissionType.finalDraft)) {
        components.push(DocumentSubmissionType.finalDraft);
      }

      return components;
    });

    const hasComponents = computed(() => {
      if (assignment.value == undefined) return false;

      if (assignment.value.type == AssignmentType.general) return false;

      return assignmentComponents.value.length > 0;
    });

    return {
      classroom,
      assignment,
      student,
      initialize,
    };
  });
